/**
 * Google Analytics 4 (gtag) — opcional vía VITE_GA_MEASUREMENT_ID.
 * Sin ID no carga scripts ni envía eventos (local/dev seguro).
 *
 * No envía PII (email/username). Solo org id/code y rol como user properties.
 */

const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim();
const IS_DEV = import.meta.env.DEV;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

export function isGoogleAnalyticsEnabled(): boolean {
  return Boolean(MEASUREMENT_ID);
}

export function getGaMeasurementId(): string {
  return MEASUREMENT_ID;
}

/**
 * Stub oficial de Google: debe hacer push de `arguments` (objeto Arguments),
 * no de un array. Si se pushea un array, gtag.js no procesa la cola y no hay hits.
 */
function installGtagStub(): void {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
}

export function initGoogleAnalytics(): void {
  if (!MEASUREMENT_ID || initialized || typeof window === 'undefined') {
    return;
  }
  initialized = true;

  installGtagStub();

  window.gtag!('js', new Date());
  // SPA: page_view manual en cambios de ruta.
  window.gtag!('config', MEASUREMENT_ID, {
    send_page_view: false,
    ...(IS_DEV ? { debug_mode: true } : {}),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  script.onerror = () => {
    if (IS_DEV) {
      console.warn(
        '[GA] No se pudo cargar gtag.js (bloqueador, red o CSP). ID=',
        MEASUREMENT_ID,
      );
    }
  };
  script.onload = () => {
    if (IS_DEV) {
      console.info('[GA] gtag.js cargado:', MEASUREMENT_ID);
    }
  };
  document.head.appendChild(script);

  if (IS_DEV) {
    console.info('[GA] init Measurement ID=', MEASUREMENT_ID);
  }
}

export function trackPageView(path: string, title?: string): void {
  if (!MEASUREMENT_ID) {
    return;
  }
  if (!window.gtag) {
    initGoogleAnalytics();
  }
  if (!window.gtag) {
    return;
  }

  const pageTitle = title || document.title;
  // Patrón recomendado para SPA: re-config con page_path.
  window.gtag('config', MEASUREMENT_ID, {
    page_path: path,
    page_title: pageTitle,
    page_location: `${window.location.origin}${path}`,
    ...(IS_DEV ? { debug_mode: true } : {}),
  });

  if (IS_DEV) {
    console.info('[GA] page_view', path);
  }
}

export function setAnalyticsContext(props: {
  organization_id?: string;
  organization_code?: string;
  role?: string;
}): void {
  if (!MEASUREMENT_ID || !window.gtag) {
    return;
  }
  window.gtag('set', 'user_properties', {
    organization_id: props.organization_id || undefined,
    organization_code: props.organization_code || undefined,
    role: props.role || undefined,
  });
}

export function clearAnalyticsContext(): void {
  if (!MEASUREMENT_ID || !window.gtag) {
    return;
  }
  window.gtag('set', 'user_properties', {
    organization_id: null,
    organization_code: null,
    role: null,
  });
}
